import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, Film, FileText, Eye, EyeOff, X } from "lucide-react";
import * as tus from "tus-js-client";

const inputCls = "w-full bg-surface border border-ink/20 focus:border-ember outline-none px-3 py-2.5 text-ink placeholder:text-ink/30 transition-colors text-sm";
const labelCls = "block text-[11px] uppercase tracking-[0.2em] text-ink/50 mb-1.5";

export default function AdminCourses({ onChanged }) {
  const [courses, setCourses] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [courseForm, setCourseForm] = useState(null);
  const [showNewCourse, setShowNewCourse] = useState(false);
  const [newCourse, setNewCourse] = useState({ title_fr: "", title_en: "", price: 109 });
  const [moduleForms, setModuleForms] = useState({});
  const [editingModule, setEditingModule] = useState(null);
  const [lessonForms, setLessonForms] = useState({});
  const [editingLesson, setEditingLesson] = useState(null);
  const [uploadPct, setUploadPct] = useState({});
  const [pdfTitles, setPdfTitles] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async (keepSelection = true) => {
    try {
      const { data } = await api.get("/admin/courses");
      setCourses(data);
      if (data.length && (!keepSelection || !selectedId)) setSelectedId((prev) => prev || data[0].id);
      if (selectedId && !data.find((c) => c.id === selectedId)) setSelectedId(data[0]?.id || null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(false); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const course = courses.find((c) => c.id === selectedId) || null;

  useEffect(() => {
    if (course) setCourseForm({ title_fr: course.title_fr, title_en: course.title_en, hook_fr: course.hook_fr, hook_en: course.hook_en, price: course.price });
  }, [selectedId, courses.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const createCourse = (e) => {
    e.preventDefault();
    run(async () => {
      const { data } = await api.post("/admin/courses", { ...newCourse, price: parseFloat(newCourse.price) || 0 });
      setSelectedId(data.id);
      setShowNewCourse(false);
      setNewCourse({ title_fr: "", title_en: "", price: 109 });
    }, "Formation créée");
  };

  const saveCourse = (e) => {
    e.preventDefault();
    run(() => api.put(`/admin/courses/${course.id}`, { ...courseForm, price: parseFloat(courseForm.price) || 0 }), "Formation enregistrée");
  };

  const toggleCourse = () => run(() => api.post(`/admin/courses/${course.id}/toggle`), course.is_active ? "Formation désactivée" : "Formation activée");

  const deleteCourse = () => {
    if (!window.confirm("Supprimer définitivement cette formation ?")) return;
    run(() => api.delete(`/admin/courses/${course.id}`), "Formation supprimée");
  };

  const createModule = (e, cid) => {
    e.preventDefault();
    const f = moduleForms[cid];
    if (!f?.title_fr) return;
    run(async () => {
      await api.post(`/admin/courses/${cid}/modules`, f);
      setModuleForms({ ...moduleForms, [cid]: { title_fr: "", title_en: "", desc_fr: "", desc_en: "" } });
    }, "Module créé");
  };

  const saveModule = (e, mid) => {
    e.preventDefault();
    run(async () => {
      await api.put(`/admin/courses/${course.id}/modules/${mid}`, editingModule);
      setEditingModule(null);
    }, "Module enregistré");
  };

  const toggleModule = (mid) => run(() => api.post(`/admin/courses/${course.id}/modules/${mid}/toggle`), "Statut du module modifié");

  const deleteModule = (mid) => {
    if (!window.confirm("Supprimer ce module et toutes ses vidéos ?")) return;
    run(() => api.delete(`/admin/courses/${course.id}/modules/${mid}`), "Module supprimé");
  };

  const createLesson = (e, mid) => {
    e.preventDefault();
    const f = lessonForms[mid];
    if (!f?.title_fr) return;
    run(async () => {
      await api.post(`/admin/courses/${course.id}/modules/${mid}/lessons`, f);
      setLessonForms({ ...lessonForms, [mid]: { title_fr: "", title_en: "", duration: "", order: "", youtube_url: "" } });
    }, "Vidéo ajoutée");
  };

  const saveLesson = (e, mid, lid) => {
    e.preventDefault();
    run(async () => {
      await api.put(`/admin/courses/${course.id}/modules/${mid}/lessons/${lid}`, editingLesson);
      setEditingLesson(null);
    }, "Vidéo enregistrée");
  };

  const toggleLesson = (mid, lid) => run(() => api.post(`/admin/courses/${course.id}/modules/${mid}/lessons/${lid}/toggle`), "Statut de la vidéo modifié");

  const deleteLesson = (mid, lid) => {
    if (!window.confirm("Supprimer définitivement cette vidéo ?")) return;
    run(() => api.delete(`/admin/courses/${course.id}/modules/${mid}/lessons/${lid}`), "Vidéo supprimée avec succès");
  };

  const uploadVideo = async (mid, lid, file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 3 Go)");
      return;
    }
    setUploadPct((p) => ({ ...p, [lid]: 0 }));
    try {
      // 1) le backend crée l'URL d'upload Cloudflare (admin authentifié, token jamais exposé)
      const { data: session } = await api.post("/admin/stream/upload-url", {
        filename: file.name, content_type: file.type || "video/mp4", size: file.size,
        course_id: course.id, module_id: mid, lesson_id: lid,
      });
      // 2) upload direct navigateur → Cloudflare (TUS, reprise possible)
      await new Promise((resolve, reject) => {
        const upload = new tus.Upload(file, {
          uploadUrl: session.upload_url,
          chunkSize: 50 * 1024 * 1024,
          retryDelays: [0, 3000, 5000, 10000, 20000],
          metadata: { filename: file.name, filetype: file.type || "video/mp4" },
          onError: (e) => reject(e),
          onProgress: (sent, total) => setUploadPct((p) => ({ ...p, [lid]: Math.round((sent / total) * 95) })),
          onSuccess: () => resolve(),
        });
        upload.start();
      });
      // 3) traitement Cloudflare : polling jusqu'à READY
      setUploadPct((p) => ({ ...p, [lid]: 97 }));
      let ready = false;
      for (let i = 0; i < 40 && !ready; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const { data: st } = await api.get(`/admin/stream/lessons/${lid}/status`);
          if (st.ready) ready = true;
          if (st.error) throw new Error(st.error);
        } catch (e) { /* polling continue */ }
      }
      setUploadPct((p) => ({ ...p, [lid]: 100 }));
      toast.success(ready ? "Vidéo uploadée et prête (Cloudflare Stream)" : "Vidéo uploadée — traitement Cloudflare en cours");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploadPct((p) => { const n = { ...p }; delete n[lid]; return n; });
    }
  };

  const saveYoutubeUrl = async (mid, l, url) => {
    if ((url || "") === (l.youtube_url || "")) return;
    run(() => api.put(`/admin/courses/${course.id}/modules/${mid}/lessons/${l.id}`, {
      title_fr: l.title_fr, title_en: l.title_en, desc_fr: l.desc_fr, desc_en: l.desc_en,
      duration: l.duration, order: l.order, youtube_url: url,
    }), url ? "Lien YouTube enregistré" : "Lien YouTube retiré");
  };

  const uploadPdf = async (mid, file, title = "") => {
    if (!file) return;
    setUploadPct((p) => ({ ...p, [`pdf-${mid}`]: 0 }));
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", title || file.name);
    try {
      await api.post(`/admin/courses/${course.id}/modules/${mid}/pdfs`, fd, {
        timeout: 300000,
        onUploadProgress: (ev) => setUploadPct((p) => ({ ...p, [`pdf-${mid}`]: Math.round((ev.loaded / (ev.total || ev.loaded)) * 100) })),
      });
      toast.success("PDF uploadé avec succès");
      setPdfTitles((t) => ({ ...t, [mid]: "" }));
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploadPct((p) => { const n = { ...p }; delete n[`pdf-${mid}`]; return n; });
    }
  };

  const togglePdf = (pid, active) => run(() => api.put(`/admin/pdfs/${pid}`, { is_active: !active }), "Statut du PDF modifié");

  const createPdfSlot = (mid) => {
    const title = (pdfTitles[mid] || "").trim();
    if (!title) { toast.error("Saisissez d'abord le titre de la fiche"); return; }
    run(async () => {
      await api.post(`/admin/courses/${course.id}/modules/${mid}/pdf-slots`, { name: title });
      setPdfTitles((t) => ({ ...t, [mid]: "" }));
    }, "Emplacement créé");
  };

  const uploadPdfFile = async (pid, file) => {
    if (!file) return;
    setUploadPct((p) => ({ ...p, [`pdffile-${pid}`]: 0 }));
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/pdfs/${pid}/file`, fd, {
        timeout: 300000,
        onUploadProgress: (ev) => setUploadPct((p) => ({ ...p, [`pdffile-${pid}`]: Math.round((ev.loaded / (ev.total || ev.loaded)) * 100) })),
      });
      toast.success("Fichier PDF ajouté");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploadPct((p) => { const n = { ...p }; delete n[`pdffile-${pid}`]; return n; });
    }
  };

  const deletePdf = (pid) => {
    if (!window.confirm("Supprimer ce PDF ?")) return;
    run(() => api.delete(`/admin/pdfs/${pid}`), "PDF supprimé");
  };

  const renamePdf = async (pid, current) => {
    const name = window.prompt("Nom du PDF :", current);
    if (!name || name === current) return;
    run(() => api.put(`/admin/pdfs/${pid}`, { name }), "PDF renommé");
  };

  return (
    <div className="mt-10 grid lg:grid-cols-12 gap-8" data-testid="admin-courses">
      {/* COURSE LIST — horizontal chips on mobile, vertical list on desktop */}
      <div className="lg:col-span-3">
        <div className="flex lg:flex-col gap-2 overflow-x-auto pb-2 lg:pb-0 -mx-4 px-4 lg:mx-0 lg:px-0">
          {courses.map((c) => (
            <button
              key={c.id}
              data-testid={`course-select-${c.id}`}
              onClick={() => setSelectedId(c.id)}
              className={`shrink-0 lg:shrink lg:w-full text-left border px-4 py-3.5 transition-colors ${selectedId === c.id ? "border-ember bg-ember/5" : "border-ink/10 bg-surface hover:border-ink/30"}`}
            >
              <p className="text-sm font-medium text-ink leading-snug whitespace-nowrap lg:whitespace-normal">{c.title_fr}</p>
              <p className="mt-1 text-xs text-ink/45 whitespace-nowrap">{c.modules.length} modules · {c.price}€ · {c.is_active ? <span className="text-green-600">Active</span> : <span className="text-ink/40">Inactive</span>}</p>
            </button>
          ))}
        </div>
        <button data-testid="new-course-btn" onClick={() => setShowNewCourse(!showNewCourse)} className="mt-4 w-full border border-dashed border-ink/25 hover:border-ember hover:text-ember text-ink/60 px-4 py-3 text-sm transition-colors flex items-center justify-center gap-2">
          <Plus size={14} /> Nouvelle formation
        </button>
        {showNewCourse && (
          <form onSubmit={createCourse} className="mt-3 border border-ink/10 bg-surface p-4 space-y-3" data-testid="new-course-form">
            <input placeholder="Titre (FR) *" value={newCourse.title_fr} onChange={(e) => setNewCourse({ ...newCourse, title_fr: e.target.value })} required className={inputCls} data-testid="new-course-title-fr" />
            <input placeholder="Titre (EN)" value={newCourse.title_en} onChange={(e) => setNewCourse({ ...newCourse, title_en: e.target.value })} className={inputCls} />
            <input placeholder="Prix (€)" type="number" step="0.01" value={newCourse.price} onChange={(e) => setNewCourse({ ...newCourse, price: e.target.value })} className={inputCls} data-testid="new-course-price" />
            <button disabled={busy} className="btn-glow w-full bg-ink hover:bg-ember text-white px-4 py-2.5 text-sm transition-colors" data-testid="new-course-submit">Créer</button>
          </form>
        )}
      </div>

      {/* COURSE EDITOR */}
      <div className="lg:col-span-9">
        {!course ? (
          <p className="text-ink/40 text-sm">Sélectionnez une formation.</p>
        ) : (
          <div className="space-y-8">
            {/* COURSE META */}
            <form onSubmit={saveCourse} className="border border-ink/10 bg-surface p-6" data-testid="course-edit-form">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
                <h2 className="font-display font-medium text-lg text-ink">Paramètres de la formation</h2>
                <div className="flex gap-2">
                  <button type="button" onClick={toggleCourse} data-testid="course-toggle-btn" className={`text-xs px-3 py-2 border transition-colors flex items-center gap-1.5 ${course.is_active ? "border-ink/20 text-ink/60 hover:text-ink" : "border-ember text-ember"}`}>
                    {course.is_active ? <><EyeOff size={13} /> Désactiver</> : <><Eye size={13} /> Activer</>}
                  </button>
                  {course.id !== "prise-de-parole" && (
                    <button type="button" onClick={deleteCourse} data-testid="course-delete-btn" className="text-xs px-3 py-2 border border-red-300 text-red-500 hover:bg-red-50 transition-colors"><Trash2 size={13} /></button>
                  )}
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className={labelCls}>Titre (FR)</label><input data-testid="course-title-fr" value={courseForm?.title_fr || ""} onChange={(e) => setCourseForm({ ...courseForm, title_fr: e.target.value })} required className={inputCls} /></div>
                <div><label className={labelCls}>Titre (EN)</label><input value={courseForm?.title_en || ""} onChange={(e) => setCourseForm({ ...courseForm, title_en: e.target.value })} className={inputCls} /></div>
                <div><label className={labelCls}>Accroche (FR)</label><textarea rows={2} value={courseForm?.hook_fr || ""} onChange={(e) => setCourseForm({ ...courseForm, hook_fr: e.target.value })} className={inputCls} /></div>
                <div><label className={labelCls}>Accroche (EN)</label><textarea rows={2} value={courseForm?.hook_en || ""} onChange={(e) => setCourseForm({ ...courseForm, hook_en: e.target.value })} className={inputCls} /></div>
                <div><label className={labelCls}>Prix (€)</label><input data-testid="course-price" type="number" step="0.01" value={courseForm?.price ?? ""} onChange={(e) => setCourseForm({ ...courseForm, price: e.target.value })} required className={inputCls} /></div>
              </div>
              <button disabled={busy} className="btn-glow mt-5 bg-ink hover:bg-ember disabled:opacity-50 text-white px-6 py-3 text-sm transition-colors" data-testid="course-save-btn">Enregistrer la formation</button>
            </form>

            {/* MODULES */}
            {course.modules.map((m) => (
              <div key={m.id} className={`border ${m.is_active ? "border-ink/10" : "border-dashed border-ink/20 opacity-80"} bg-surface`} data-testid={`admin-module-${m.id}`}>
                <div className="px-5 py-4 border-b border-ink/10 flex flex-wrap items-center gap-3">
                  <span className="font-serif italic text-3xl text-ember/80">{m.num}</span>
                  <div className="flex-1 min-w-[180px]">
                    <p className="font-display font-medium text-ink text-sm">{m.title_fr}</p>
                    <p className="text-xs text-ink/40">
                      {m.module_type === "pdf"
                        ? <>{m.pdfs.length} fiche(s) PDF · ordre {m.order} · {m.is_active ? <span className="text-green-600">Publié</span> : <span>Brouillon (min. 4 fiches PDF pour publier)</span>}</>
                        : <>{m.lessons.length} vidéo(s) · ordre {m.order} · {m.is_active ? <span className="text-green-600">Publié</span> : <span>Brouillon (min. 4 vidéos pour publier)</span>}</>}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditingModule(editingModule?.id === m.id ? null : { id: m.id, title_fr: m.title_fr, title_en: m.title_en, desc_fr: m.desc_fr, desc_en: m.desc_en, order: m.order })} className="p-2 border border-ink/15 text-ink/50 hover:text-ink transition-colors" data-testid={`module-edit-${m.id}`}><Pencil size={13} /></button>
                    <button onClick={() => toggleModule(m.id)} className="p-2 border border-ink/15 text-ink/50 hover:text-ember transition-colors" data-testid={`module-toggle-${m.id}`}>{m.is_active ? <EyeOff size={13} /> : <Eye size={13} />}</button>
                    <button onClick={() => deleteModule(m.id)} className="p-2 border border-ink/15 text-ink/50 hover:text-red-500 hover:border-red-300 transition-colors" data-testid={`module-delete-${m.id}`}><Trash2 size={13} /></button>
                  </div>
                </div>

                {editingModule?.id === m.id && (
                  <form onSubmit={(e) => saveModule(e, m.id)} className="px-5 py-4 bg-panel/60 border-b border-ink/10 grid md:grid-cols-2 gap-3" data-testid={`module-edit-form-${m.id}`}>
                    <input value={editingModule.title_fr} onChange={(e) => setEditingModule({ ...editingModule, title_fr: e.target.value })} placeholder="Titre (FR)" required className={inputCls} data-testid={`module-edit-title-${m.id}`} />
                    <input value={editingModule.title_en} onChange={(e) => setEditingModule({ ...editingModule, title_en: e.target.value })} placeholder="Titre (EN)" className={inputCls} />
                    <input value={editingModule.desc_fr} onChange={(e) => setEditingModule({ ...editingModule, desc_fr: e.target.value })} placeholder="Description (FR)" className={inputCls} />
                    <input value={editingModule.desc_en} onChange={(e) => setEditingModule({ ...editingModule, desc_en: e.target.value })} placeholder="Description (EN)" className={inputCls} />
                    <input type="number" value={editingModule.order} onChange={(e) => setEditingModule({ ...editingModule, order: parseInt(e.target.value) || 0 })} placeholder="Ordre" className={inputCls} data-testid={`module-edit-order-${m.id}`} />
                    <div className="flex gap-2">
                      <button disabled={busy} className="bg-ink hover:bg-ember text-white px-4 py-2.5 text-sm transition-colors">Enregistrer</button>
                      <button type="button" onClick={() => setEditingModule(null)} className="border border-ink/20 text-ink/60 px-4 py-2.5 text-sm"><X size={14} /></button>
                    </div>
                  </form>
                )}

                {/* LESSONS */}
                {m.module_type !== "pdf" && (
                <ul>
                  {m.lessons.map((l) => (
                    <li key={l.id} className={`px-4 md:px-5 py-3.5 border-b border-ink/5 ${!l.is_active ? "opacity-50" : ""}`} data-testid={`admin-lesson-${l.id}`}>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                        <span className="text-xs text-ink/35 w-6">#{l.order}</span>
                        <Film size={14} className={l.has_video ? "text-ember" : "text-ink/20"} />
                        <span className="flex-1 text-sm text-ink/80 min-w-[140px]">{l.title_fr}</span>
                        {l.provider && (
                          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${l.provider === "youtube" ? "border-red-300 text-red-500" : l.provider === "cloudflare" ? "border-orange-400 text-orange-600" : "border-ink/20 text-ink/50"}`}>
                            {l.provider === "youtube" ? "YouTube" : l.provider === "cloudflare" ? (l.cf_status === "ready" ? "Cloudflare" : `Cloudflare (${l.cf_status || "…"})`) : "Fichier"}
                          </span>
                        )}
                        <span className="text-xs text-ink/40 w-full sm:w-auto sm:max-w-[160px] truncate order-last sm:order-none">
                          {l.provider === "youtube" ? "Lien YouTube enregistré" : l.provider === "cloudflare" ? (l.cf_status === "ready" ? "Prête à la lecture" : "Traitement Cloudflare…") : l.has_video ? l.video_name : "Aucune vidéo"}
                        </span>
                        <div className="flex items-center gap-2 flex-wrap w-full lg:w-auto">
                          <input
                            data-testid={`youtube-url-${l.id}`}
                            defaultValue={l.youtube_url || ""}
                            key={`${l.id}-${l.youtube_url || ""}`}
                            placeholder="Lien YouTube (non répertorié)"
                            className="w-full sm:w-56 bg-surface border border-ink/20 focus:border-ember outline-none px-2.5 py-1.5 text-xs text-ink placeholder:text-ink/30 transition-colors"
                            onBlur={(e) => saveYoutubeUrl(m.id, l, e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveYoutubeUrl(m.id, l, e.target.value); } }}
                          />
                          {uploadPct[l.id] !== undefined ? (
                            <span className="text-xs text-ember w-24" data-testid={`upload-progress-${l.id}`}>
                              <span className="block h-1.5 bg-ink/10 w-full mb-1"><span className="block h-full bg-ember" style={{ width: `${uploadPct[l.id]}%` }} /></span>
                              {uploadPct[l.id]}%
                            </span>
                          ) : (
                            <label title="Uploader une vidéo (Cloudflare Stream — direct, gros fichiers OK)" className="inline-flex items-center gap-1.5 border border-ink/20 hover:border-ember hover:text-ember px-3 py-1.5 text-xs text-ink/60 cursor-pointer transition-colors">
                              <Upload size={12} /> {l.has_video ? "Remplacer" : "Uploader"}
                              <input data-testid={`upload-video-${l.id}`} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={(e) => uploadVideo(m.id, l.id, e.target.files[0])} />
                            </label>
                          )}
                          <button onClick={() => setEditingLesson(editingLesson?.id === l.id ? null : { id: l.id, mid: m.id, title_fr: l.title_fr, title_en: l.title_en, desc_fr: l.desc_fr, desc_en: l.desc_en, duration: l.duration, order: l.order, youtube_url: l.youtube_url || "" })} className="p-1.5 border border-ink/15 text-ink/50 hover:text-ink transition-colors" data-testid={`lesson-edit-${l.id}`}><Pencil size={12} /></button>
                          <button onClick={() => toggleLesson(m.id, l.id)} className="p-1.5 border border-ink/15 text-ink/50 hover:text-ember transition-colors" data-testid={`lesson-toggle-${l.id}`}>{l.is_active ? <EyeOff size={12} /> : <Eye size={12} />}</button>
                          <button onClick={() => deleteLesson(m.id, l.id)} className="p-1.5 border border-ink/15 text-ink/50 hover:text-red-500 hover:border-red-300 transition-colors" data-testid={`lesson-delete-${l.id}`}><Trash2 size={12} /></button>
                        </div>
                      </div>
                      {editingLesson?.id === l.id && (
                        <form onSubmit={(e) => saveLesson(e, m.id, l.id)} className="mt-3 grid md:grid-cols-3 gap-3" data-testid={`lesson-edit-form-${l.id}`}>
                          <input value={editingLesson.title_fr} onChange={(e) => setEditingLesson({ ...editingLesson, title_fr: e.target.value })} placeholder="Titre (FR)" required className={inputCls} data-testid={`lesson-edit-title-${l.id}`} />
                          <input value={editingLesson.title_en} onChange={(e) => setEditingLesson({ ...editingLesson, title_en: e.target.value })} placeholder="Titre (EN)" className={inputCls} />
                          <input value={editingLesson.duration} onChange={(e) => setEditingLesson({ ...editingLesson, duration: e.target.value })} placeholder="Durée (ex. 12:30)" className={inputCls} />
                          <input value={editingLesson.desc_fr} onChange={(e) => setEditingLesson({ ...editingLesson, desc_fr: e.target.value })} placeholder="Description (FR)" className={inputCls} />
                          <input value={editingLesson.desc_en} onChange={(e) => setEditingLesson({ ...editingLesson, desc_en: e.target.value })} placeholder="Description (EN)" className={inputCls} />
                          <input type="number" value={editingLesson.order} onChange={(e) => setEditingLesson({ ...editingLesson, order: parseInt(e.target.value) || 0 })} placeholder="Ordre" className={inputCls} data-testid={`lesson-edit-order-${l.id}`} />
                          <input value={editingLesson.youtube_url || ""} onChange={(e) => setEditingLesson({ ...editingLesson, youtube_url: e.target.value })} placeholder="Lien YouTube (non répertorié)" className={`${inputCls} md:col-span-2`} data-testid={`lesson-edit-youtube-${l.id}`} />
                          <div className="flex gap-2 md:col-span-3">
                            <button disabled={busy} className="bg-ink hover:bg-ember text-white px-4 py-2 text-xs transition-colors">Enregistrer</button>
                            <button type="button" onClick={() => setEditingLesson(null)} className="border border-ink/20 text-ink/60 px-4 py-2 text-xs"><X size={12} /></button>
                          </div>
                        </form>
                      )}
                    </li>
                  ))}
                </ul>
                )}

                {/* ADD LESSON */}
                {m.module_type !== "pdf" && (
                <form onSubmit={(e) => createLesson(e, m.id)} className="px-4 md:px-5 py-4 bg-panel/40 flex flex-wrap items-stretch sm:items-center gap-3" data-testid={`add-lesson-form-${m.id}`}>
                  <input value={lessonForms[m.id]?.title_fr || ""} onChange={(e) => setLessonForms({ ...lessonForms, [m.id]: { ...lessonForms[m.id], title_fr: e.target.value } })} placeholder="Titre de la nouvelle vidéo (FR) *" required className={`${inputCls} w-full sm:flex-1 sm:min-w-[200px]`} data-testid={`add-lesson-title-${m.id}`} />
                  <input value={lessonForms[m.id]?.title_en || ""} onChange={(e) => setLessonForms({ ...lessonForms, [m.id]: { ...lessonForms[m.id], title_en: e.target.value } })} placeholder="Titre (EN)" className={`${inputCls} w-full sm:w-44`} />
                  <input value={lessonForms[m.id]?.duration || ""} onChange={(e) => setLessonForms({ ...lessonForms, [m.id]: { ...lessonForms[m.id], duration: e.target.value } })} placeholder="Durée" className={`${inputCls} w-full sm:w-24`} />
                  <input value={lessonForms[m.id]?.youtube_url || ""} onChange={(e) => setLessonForms({ ...lessonForms, [m.id]: { ...lessonForms[m.id], youtube_url: e.target.value } })} placeholder="Lien YouTube (optionnel)" className={`${inputCls} w-full sm:w-64`} data-testid={`add-lesson-youtube-${m.id}`} />
                  <button disabled={busy} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-ink hover:bg-ember text-white px-4 py-2.5 text-xs transition-colors" data-testid={`add-lesson-submit-${m.id}`}><Plus size={13} /> Ajouter la vidéo</button>
                </form>
                )}

                {/* PDFS */}
                <div className="px-5 py-4 bg-panel/60 border-t border-ink/10">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-ink/40 mb-3">Fiches PDF du module</p>
                  <div className="space-y-2">
                    {course.pdfs.filter((p) => p.module_id === m.id).map((p) => (
                      <div key={p.id} className={`flex flex-wrap items-center gap-3 text-sm ${!p.is_active ? "opacity-50" : ""}`} data-testid={`admin-pdf-${p.id}`}>
                        <FileText size={14} className={p.has_file ? "text-ember" : "text-ink/25"} />
                        <span className="flex-1 text-ink/75 min-w-[140px]">{p.name}</span>
                        {!p.has_file && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-dashed border-ink/25 text-ink/45" data-testid={`pdf-missing-${p.id}`}>Fichier manquant</span>}
                        {uploadPct[`pdffile-${p.id}`] !== undefined ? (
                          <span className="text-xs text-ember" data-testid={`pdf-file-progress-${p.id}`}>{uploadPct[`pdffile-${p.id}`]}%</span>
                        ) : (
                          <label className="inline-flex items-center gap-1.5 border border-ink/20 hover:border-ember hover:text-ember px-2.5 py-1.5 text-xs text-ink/60 cursor-pointer transition-colors">
                            <Upload size={12} /> {p.has_file ? "Remplacer" : "Ajouter le fichier"}
                            <input data-testid={`pdf-file-upload-${p.id}`} type="file" accept="application/pdf" className="hidden" onChange={(e) => { uploadPdfFile(p.id, e.target.files[0]); e.target.value = ""; }} />
                          </label>
                        )}
                        <button onClick={() => renamePdf(p.id, p.name)} className="p-1.5 border border-ink/15 text-ink/50 hover:text-ink" data-testid={`pdf-rename-${p.id}`}><Pencil size={12} /></button>
                        <button onClick={() => togglePdf(p.id, p.is_active)} className="p-1.5 border border-ink/15 text-ink/50 hover:text-ember" data-testid={`pdf-toggle-${p.id}`}>{p.is_active ? <EyeOff size={12} /> : <Eye size={12} />}</button>
                        <button onClick={() => deletePdf(p.id)} className="p-1.5 border border-ink/15 text-ink/50 hover:text-red-500 hover:border-red-300" data-testid={`pdf-delete-${p.id}`}><Trash2 size={12} /></button>
                      </div>
                    ))}
                  </div>
                  {m.module_type === "pdf" && (
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <input
                        value={pdfTitles[m.id] || ""}
                        onChange={(e) => setPdfTitles({ ...pdfTitles, [m.id]: e.target.value })}
                        placeholder="Titre de la fiche (ex. Fiche d'exercice — prise de parole)"
                        className={`${inputCls} flex-1 min-w-[220px] max-w-md`}
                        data-testid={`pdf-title-${m.id}`}
                      />
                      <button
                        type="button"
                        onClick={() => createPdfSlot(m.id)}
                        disabled={busy}
                        data-testid={`add-pdf-slot-${m.id}`}
                        className="inline-flex items-center gap-1.5 bg-ink hover:bg-ember text-white px-3 py-2.5 text-xs transition-colors"
                      >
                        <Plus size={12} /> Créer l'emplacement
                      </button>
                    </div>
                  )}
                  {uploadPct[`pdf-${m.id}`] !== undefined ? (
                    <p className="mt-3 text-xs text-ember">Upload PDF… {uploadPct[`pdf-${m.id}`]}%</p>
                  ) : (
                    <label className="mt-3 inline-flex items-center gap-2 border border-dashed border-ink/25 hover:border-ember px-4 py-2 text-xs text-ink/60 hover:text-ember cursor-pointer transition-colors">
                      <Upload size={12} /> {m.module_type === "pdf" ? "Ajouter un PDF" : "Uploader un PDF"}
                      <input data-testid={`upload-pdf-${m.id}`} type="file" accept="application/pdf" className="hidden" onChange={(e) => { uploadPdf(m.id, e.target.files[0], pdfTitles[m.id] || ""); e.target.value = ""; }} />
                    </label>
                  )}
                </div>
              </div>
            ))}

            {/* ADD MODULE */}
            <form onSubmit={(e) => createModule(e, course.id)} className="border border-dashed border-ink/25 p-5" data-testid="add-module-form">
              <p className="text-[11px] uppercase tracking-[0.2em] text-ink/40 mb-3">Nouveau module</p>
              <div className="grid md:grid-cols-2 gap-3">
                <input value={moduleForms[course.id]?.title_fr || ""} onChange={(e) => setModuleForms({ ...moduleForms, [course.id]: { ...moduleForms[course.id], title_fr: e.target.value } })} placeholder="Titre du module (FR) *" required className={inputCls} data-testid="add-module-title-fr" />
                <input value={moduleForms[course.id]?.title_en || ""} onChange={(e) => setModuleForms({ ...moduleForms, [course.id]: { ...moduleForms[course.id], title_en: e.target.value } })} placeholder="Titre (EN)" className={inputCls} />
                <input value={moduleForms[course.id]?.desc_fr || ""} onChange={(e) => setModuleForms({ ...moduleForms, [course.id]: { ...moduleForms[course.id], desc_fr: e.target.value } })} placeholder="Description (FR)" className={inputCls} />
                <input value={moduleForms[course.id]?.desc_en || ""} onChange={(e) => setModuleForms({ ...moduleForms, [course.id]: { ...moduleForms[course.id], desc_en: e.target.value } })} placeholder="Description (EN)" className={inputCls} />
              </div>
              <div className="mt-3">
                <label className={labelCls}>Type de contenu</label>
                <select
                  value={moduleForms[course.id]?.module_type || "video"}
                  onChange={(e) => setModuleForms({ ...moduleForms, [course.id]: { ...moduleForms[course.id], module_type: e.target.value } })}
                  className={`${inputCls} max-w-xs`}
                  data-testid="add-module-type"
                >
                  <option value="video">Vidéos (Cloudflare Stream)</option>
                  <option value="pdf">Fiches PDF téléchargeables</option>
                </select>
              </div>
              <button disabled={busy} className="btn-glow mt-4 bg-ink hover:bg-ember text-white px-5 py-2.5 text-sm transition-colors inline-flex items-center gap-2" data-testid="add-module-submit"><Plus size={14} /> Créer le module</button>
              <p className="mt-2 text-xs text-ink/40">Le module est créé en brouillon : il devient publiable avec au moins 4 {(moduleForms[course.id]?.module_type || "video") === "pdf" ? "fiches PDF actives" : "vidéos actives"}.</p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
